// U3 · Your week (QU3_Week): the recap rows, the one question (voice or typed -> POST /api/week/answer) and the week
// chest (POST /api/week/chest: ready once the Sunday recap is out and 3+ days were lit). state.week is this week's
// recap when ready, else last week's, else a live preview (ready: false).
import { useState } from 'preact/hooks';
import { C } from '@shared/content/characters';
import { LINES } from '@shared/content/lines';
import { POINTS } from '@shared/rules';
import { useApp, weekAnswer, weekChest, toast } from '../../store';
import { back, go } from '../../router';
import { Screen, ScrollArea, IconButton, XFace, TalkButton, TextButton, PrimaryButton, SecondaryButton, Icon } from '../../components';
import { nextStop, plural, stopName } from './route';
import { US } from './copy';
import s from './us.module.css';
import { say } from '../../lib/say';

export function WeekScreen() {
  const st = useApp((x) => x.state);
  const [busy, setBusy] = useState<'answer' | 'chest' | null>(null);
  const [typing, setTyping] = useState(false);
  const [text, setText] = useState('');
  const [gained, setGained] = useState<number | null>(null);
  if (!st) return <Screen theme="margo" id="U3" />;
  const ch = st.ch, c = C[ch], w = st.week, L = LINES[ch];

  const answer = async (body: { text?: string; audio?: { b64: string; mime: string } }) => {
    if (busy) return;
    setBusy('answer');
    const r = await weekAnswer(body);
    setBusy(null);
    if (r) { setTyping(false); setText(''); }
  };
  const open = async () => {
    if (busy) return;
    setBusy('chest');
    const pts = await weekChest();
    setBusy(null);
    if (pts != null) setGained(pts);
  };

  const rows = w ? [
    `${plural(w.lit, 'day')} lit`,
    `${plural(w.cards, 'card')} done`,
    `Started on time ${plural(w.onTime, 'time')} (last week: ${w.onTimePrev})`,
    ...(w.highlight ? [w.highlight] : []),
  ] : [];
  const next = nextStop(st.buddy);

  return (
    <Screen theme={ch} id="U3">
      <header class={s.bar}>
        <IconButton icon="back" label="Back" class={s.back} onClick={() => back('U1')} />
        <div>
          <h1 class={s.barTitle}>Your week</h1>
          {w && <p class={s.barSub}>{w.label}</p>}
        </div>
      </header>
      <ScrollArea class={s.weekScroll}>
        <div class={`${s.list} ${s.weekList}`}>
          <div class={s.intro}>
            <XFace ch={ch} e={4} s={52} ring="var(--acc)" alt="" />
            <p class={s.bubble}><span class="xm-sr-only">{c.name}: </span>{say(L.weekIntro, { n: c.name })}</p>
          </div>

          {!w && <section class={`${s.card} ${s.ask}`}><p class={s.chestSub}>{US.weekFirst}</p></section>}

          {w && (
            <section class={`${s.card} ${s.rows}`} aria-label="This week">
              {rows.map((t) => (
                <div class={s.check} key={t}>
                  <span class={s.tick} aria-hidden="true"><Icon name="check" size={14} strokeWidth={3} /></span>
                  <span class={s.checkT}>{t}</span>
                </div>
              ))}
            </section>
          )}

          {w && !w.ready && <section class={`${s.card} ${s.ask}`}><p class={s.chestSub}>{US.notReady}</p></section>}

          {w && w.ready && (
            <section class={`${s.card} ${s.ask}`} aria-label={US.question}>
              <p class={s.askQ}>{US.question}</p>
              {!w.answer && !typing && (
                <>
                  <TalkButton
                    name={c.name} disabled={busy === 'answer'} idleLabel={busy === 'answer' ? 'One moment…' : US.holdHint}
                    onAudio={(clip) => answer({ audio: { b64: clip.b64, mime: clip.mime } })}
                    onError={(e) => { toast(e.message || 'Could not start the microphone.'); setTyping(true); }}
                    onTooShort={() => toast('Hold to talk, or tap once to start and once to stop.')}
                  />
                  <TextButton weight={600} size={14} onClick={() => setTyping(true)}>Type instead</TextButton>
                </>
              )}
              {!w.answer && typing && (
                <form class={s.typeRow} onSubmit={(e) => { e.preventDefault(); if (text.trim()) void answer({ text: text.trim() }); }}>
                  <input
                    type="text" aria-label="Your answer" placeholder="One sentence is plenty" value={text} autoFocus enterKeyHint="send"
                    maxLength={1000} onInput={(e) => setText((e.currentTarget as HTMLInputElement).value)}
                  />
                  <IconButton icon="send" label="Send" variant="solid" size={48} square busy={busy === 'answer'} disabled={!text.trim()} onClick={() => { if (text.trim()) void answer({ text: text.trim() }); }} />
                </form>
              )}
              {w.answer && (
                <>
                  <p class={s.you}><span class="xm-sr-only">You: </span>{w.answer}</p>
                  {w.reply && (
                    <div class={s.reply}>
                      <XFace ch={ch} e={2} s={30} ring="var(--acc)" alt="" />
                      <p class={s.replyT}><span class="xm-sr-only">{c.name}: </span>{w.reply}</p>
                    </div>
                  )}
                </>
              )}
            </section>
          )}

          {w && (
            <section class={`${s.card} ${s.chest}`} data-st={w.chest} aria-label={US.chest} aria-live="polite">
              {w.chest !== 'opened' && (
                <>
                  <div class={w.chest === 'ready' ? s.gift : undefined}>
                    <Icon name="gift" size={72} strokeWidth={1.5} color={w.chest === 'ready' ? 'var(--acc)' : '#8C857B'} fill="rgba(255,255,255,0.06)" />
                  </div>
                  <p class={s.chestT}>{US.chest}</p>
                  <p class={s.chestSub}>{w.chest === 'ready' ? US.chestReady(w.lit) : US.chestLocked(w.lit)}</p>
                  {w.chest === 'ready' && <PrimaryButton h={54} busy={busy === 'chest'} style={{ alignSelf: 'stretch', marginTop: 4 }} onClick={() => void open()}>Open</PrimaryButton>}
                </>
              )}
              {w.chest === 'opened' && (
                <>
                  <span class={s.pts}>{`+${gained ?? POINTS.weekChest} points`}</span>
                  <p class={s.chestLine}>{`${gained ?? POINTS.weekChest} points closer to level ${st.level.level + 1}`}</p>
                  {next && <p class={s.chestSub}>{`Next stop: ${stopName(next)}.`}</p>}
                  <div class={s.two}>
                    <SecondaryButton h={50} grow={1} onClick={() => go('SH', { mode: 'week', from: 'U3' })}>Share your week</SecondaryButton>
                    <PrimaryButton h={50} grow={1} onClick={() => go('U1')}>Back to Us</PrimaryButton>
                  </div>
                </>
              )}
            </section>
          )}
        </div>
      </ScrollArea>
    </Screen>
  );
}
